import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const TeamContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const TeamCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 2.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || corporateTheme.primaryGradient};
  }
`;

const RoleIcon = styled.div`
  font-size: 0.9rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.background};
  background: ${corporateTheme.primaryGradient};
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0 auto 1.5rem;
  font-family: ${corporateTheme.fontFamily};
  letter-spacing: 0.5px;
  line-height: 1;
`;

const RoleTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const RoleDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const SkillsList = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
`;

const Skill = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  font-family: ${corporateTheme.fontFamily};
  
  &:before {
    content: "•";
    color: ${corporateTheme.primary};
    font-weight: bold;
    margin-right: 0.8rem;
    margin-top: 0.1rem;
  }
`;

const OrganizationSection = styled(motion.div)`
  background: rgba(37, 99, 235, 0.05);
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid rgba(37, 99, 235, 0.15);
  box-shadow: ${corporateTheme.cardShadow};
  margin-top: 4rem;
`;

const OrgTitle = styled.h3`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const OrgGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const DepartmentCard = styled.div`
  background: ${corporateTheme.cardBackground};
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 1.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const DepartmentTitle = styled.h4`
  font-size: 1.2rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 0.5rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const DepartmentCount = styled.div`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  margin-bottom: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const DepartmentRole = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  line-height: 1.4;
  font-family: ${corporateTheme.fontFamily};
`;

const TeamSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const keyRoles = [
    {
      icon: corporateLabels.executive,
      title: "CEO / Managing Director",
      description: "Visionary leader with extensive experience in digital marketing and affiliate marketing, proven track record of scaling businesses in Asia.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.primary} 0%, ${corporateTheme.primary} 100%)`,
      skills: [
        "Strategic vision and leadership",
        "Eastern Asian market expertise",
        "Investor relations and fundraising",
        "Business development and partnerships"
      ]
    },
    {
      icon: corporateLabels.technology,
      title: "Head of Technology",
      description: "Technical expert in building and managing scalable SaaS platforms, data analytics, and cybersecurity systems.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.secondary} 0%, ${corporateTheme.success} 100%)`,
      skills: [
        "Platform architecture and development",
        "Data analytics and fraud detection",
        "Cybersecurity and compliance",
        "Team management and scaling"
      ]
    },
    {
      icon: corporateLabels.sales,
      title: "Head of Sales & BD",
      description: "Proven success in B2B sales within Eastern Asian digital advertising and e-commerce sectors.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.tertiary} 0%, ${corporateTheme.error} 100%)`,
      skills: [
        "B2B sales and negotiation",
        "Regional market knowledge",
        "Relationship building",
        "Revenue growth strategies"
      ]
    },
    {
      icon: corporateLabels.marketing,
      title: "Head of Affiliate Management",
      description: "Extensive network within affiliate marketing community with deep understanding of affiliate needs and fraud prevention.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quaternary} 0%, ${corporateTheme.warning} 100%)`,
      skills: [
        "Affiliate network development",
        "Performance optimization",
        "Fraud detection and prevention",
        "Community building"
      ]
    },
    {
      icon: corporateLabels.content,
      title: "Head of Content & SEO",
      description: "Proven track record in building and scaling high-traffic, valuable content websites in competitive markets.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quinary} 0%, ${corporateTheme.accent} 100%)`,
      skills: [
        "Content strategy and creation",
        "SEO and organic growth",
        "Team management",
        "Performance analytics"
      ]
    },
    {
      icon: corporateLabels.operations,
      title: "Regional Managers",
      description: "Native speakers with deep cultural understanding and business networks in respective target countries.",
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.senary} 0%, ${corporateTheme.accentLight} 100%)`,
      skills: [
        "Local market expertise",
        "Cultural adaptation",
        "Business development",
        "Partnership management"
      ]
    }
  ];

  const departments = [
    {
      title: "Leadership",
      count: "6",
      role: "Executive team and department heads"
    },
    {
      title: "Technology",
      count: "5-7",
      role: "Platform development and maintenance"
    },
    {
      title: "Content Team",
      count: "8-12",
      role: "Writers, editors, SEO specialists"
    },
    {
      title: "Sales & BD",
      count: "6-8",
      role: "Account managers and sales team"
    },
    {
      title: "Marketing",
      count: "4-6",
      role: "Digital marketing and PR"
    },
    {
      title: "Operations",
      count: "3-5",
      role: "Finance, legal, and admin"
    }
  ];

  return (
    <TeamContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Team & Organization
        </Title>

        <TeamGrid>
          {keyRoles.map((role, index) => (
            <TeamCard
              key={index}
              variants={itemVariants}
              color={role.color}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <RoleIcon>{role.icon}</RoleIcon>
              <RoleTitle>{role.title}</RoleTitle>
              <RoleDescription>{role.description}</RoleDescription>
              <SkillsList>
                {role.skills.map((skill, skillIndex) => (
                  <Skill key={skillIndex}>{skill}</Skill>
                ))}
              </SkillsList>
            </TeamCard>
          ))}
        </TeamGrid>

        <OrganizationSection variants={itemVariants}>
          <OrgTitle>Organizational Structure (First 24 Months)</OrgTitle>
          <OrgGrid>
            {departments.map((dept, index) => (
              <DepartmentCard key={index}>
                <DepartmentTitle>{dept.title}</DepartmentTitle>
                <DepartmentCount>{dept.count}</DepartmentCount>
                <DepartmentRole>{dept.role}</DepartmentRole>
              </DepartmentCard>
            ))}
          </OrgGrid>
        </OrganizationSection>
      </motion.div>
    </TeamContainer>
  );
};

export default TeamSection; 